import { CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { AlertCircle, ImagePlus, X } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PendingImage } from '../hooks/useImageUploads';
import { formatBytes } from '../utils';
import { FieldLabel } from './FieldLabel';

interface ImageInputPickerProps {
  images: PendingImage[];
  maxImages: number;
  maxImageBytes: number | null;
  allowedMimeTypes: string[];
  onAdd: (files: File[]) => void;
  onRemove: (localId: string) => void;
}

export const ImageInputPicker: React.FC<ImageInputPickerProps> = ({
  images,
  maxImages,
  maxImageBytes,
  allowedMimeTypes,
  onAdd,
  onRemove,
}) => {
  const { t, i18n } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);
  const remaining = Math.max(0, maxImages - images.length);

  const accept = (files: File[]) => {
    setRejected(null);
    const valid = files.filter((file) => {
      if (allowedMimeTypes.length && !allowedMimeTypes.includes(file.type)) return false;
      return !maxImageBytes || file.size <= maxImageBytes;
    });
    if (valid.length < files.length) setRejected(t('services.generateVideo.imageRejected'));
    if (valid.length) onAdd(valid.slice(0, remaining));
  };

  return (
    <div className="space-y-2">
      <FieldLabel
        label={t('services.generateVideo.imageInputLabel')}
        description={t('services.generateVideo.imageInputHint')}
      />
      {remaining > 0 && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            accept(Array.from(event.dataTransfer.files));
          }}
          className={`flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition ${
            dragging
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
              : 'border-gray-300 hover:border-blue-400 dark:border-slate-700 dark:hover:border-blue-500'
          }`}
        >
          <ImagePlus className="text-gray-400 dark:text-slate-500" size={28} />
          <Typography variant="body2" className="text-gray-700 dark:text-slate-200">
            {t('services.generateVideo.imageDrop')}
          </Typography>
          <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
            {t('services.generateVideo.imageHint', {
              types: allowedMimeTypes.map((type) => type.replace('image/', '')).join(', '),
              size: maxImageBytes ? formatBytes(maxImageBytes, i18n.language) : '-',
              count: maxImages,
            })}
          </Typography>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple={maxImages > 1}
        accept={allowedMimeTypes.join(',')}
        onChange={(event) => {
          accept(Array.from(event.target.files ?? []));
          event.target.value = '';
        }}
      />
      {rejected && (
        <Typography variant="caption" className="block text-red-600 dark:text-red-400">
          {rejected}
        </Typography>
      )}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {images.map((image) => (
            <div
              key={image.localId}
              className="relative h-24 w-24 overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700"
            >
              <img
                src={image.previewUrl}
                alt={image.fileName}
                className="h-full w-full object-cover"
              />
              {image.status === 'uploading' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <CircularProgress size={22} className="text-white" />
                </div>
              )}
              {image.status === 'failed' && (
                <Tooltip title={image.error ?? t('services.generateVideo.imageUploadFailed')}>
                  <div className="absolute inset-0 flex items-center justify-center bg-red-900/60">
                    <AlertCircle className="text-white" size={22} />
                  </div>
                </Tooltip>
              )}
              <IconButton
                size="small"
                onClick={() => onRemove(image.localId)}
                aria-label={t('services.generateVideo.removeImage')}
                className="absolute right-1 top-1 bg-black/50 text-white hover:bg-black/70"
              >
                <X size={14} />
              </IconButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
